import React, { useState, useEffect } from 'react';
import { ref, getDownloadURL } from 'firebase/storage';
import { storage } from '../firebase';
import Button from '../ui/Button';
import Icon from '../ui/Icon';
import { ICONS } from '../ui/icon-map';
import Dialog from '../ui/Dialog/Dialog';
import EmptyState from '../ui/EmptyState';
import { toast } from '../ui/feedback';
import LoadingSpinner from './LoadingSpinner';
import logger from '../utils/logger';

const LicenseModal = ({ url: plateId, onClose }) => {
    const [downloadUrl, setDownloadUrl] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Initial ID Resolution on mount using Firebase Storage (Reliable & Stable)
    useEffect(() => {
        if (!plateId) return;
        let isCancelled = false;
        // The modal stays mounted between cars, so forget the previous car's
        // file first: otherwise a car with no licence would show, print and
        // download the last car's PDF under its own plate (audit FLT-03).
        setDownloadUrl(null);
        setError(null);
        setLoading(true);
        const resolveLicense = async () => {
            // Check if it's already a full URL (Legacy fallback/transition)
            if (plateId.startsWith('http')) {
                setDownloadUrl(plateId);
                setLoading(false);
                return;
            }

            const bucket = storage.app.options.storageBucket;

            try {
                // 1. Try 'licenses/' folder (Preferred)
                const storageRef = ref(storage, `licenses/${plateId}.pdf`);
                const url = await getDownloadURL(storageRef);
                
                if (!isCancelled) {
                    setDownloadUrl(url);
                }
            } catch (err) {
                
                try {
                    // 2. Try Root (Fallback)
                    const rootRef = ref(storage, `${plateId}.pdf`);
                    const rootUrl = await getDownloadURL(rootRef);
                    if (!isCancelled) {
                        setDownloadUrl(rootUrl);
                    }
                } catch (rootErr) {
                    
                    try {
                        // 3. Try Image Fallback
                        const imgRef = ref(storage, `licenses/${plateId}.png`);
                        const imgUrl = await getDownloadURL(imgRef);
                        if (!isCancelled) setDownloadUrl(imgUrl);
                    } catch (imgErr) {
                        if (!isCancelled) {
                            // Developer detail goes to the log; the user gets a plain message.
                            logger.warn(`[LicenseModal] File [${plateId}.pdf] not found in bucket [${bucket}]. `
                                + `Checked 'licenses/${plateId}.pdf', '${plateId}.pdf' and 'licenses/${plateId}.png' `
                                + '(names are case-sensitive; Storage rules must allow reading).', imgErr);
                            setError(`No license file was found for ${plateId}. Please contact an administrator if it should be available.`);
                        }
                    }
                }
            } finally {
                if (!isCancelled) setLoading(false);
            }
        };

        resolveLicense();
        return () => { isCancelled = true; };
    }, [plateId]);

    if (!plateId) return null;

    const printImage = (imgUrl) => {
        const isPdf = imgUrl.includes('.pdf');
        const printWindow = window.open('', '_blank');
        if (!printWindow) {
            toast.error('Pop-up blocked! Please allow pop-ups to print.');
            return;
        }

        // --- 🏆 ULTIMATE PERFECT AUTO-PRINT (PHASE 3) ---
        // This opens a window that handles its own Fetch/Blob lifecycle.
        // It ensures the Blob URL is native to the printer's context.
        printWindow.document.write(`
            <html>
            <head><title>Preparing Print...</title>
            <style>
                body, html { margin: 0; padding: 0; width: 100%; height: 100%; overflow: hidden; background: #fff; display: flex; flex-direction: column; align-items: center; justify-content: center; font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto; }
                .loader { border: 4px solid #f3f3f3; border-top: 4px solid #ef4444; border-radius: 50%; width: 40px; height: 40px; animation: spin 0.8s linear infinite; }
                @keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
                p { color: #666; font-size: 14px; margin-top: 15px; font-weight: 600; }
                iframe, img { display: none; width: 100%; height: 100%; border: none; }
            </style>
            </head>
            <body>
                <div id="loadingUI">
                    <div class="loader"></div>
                    <p>Preparing document for printing...</p>
                </div>
                <script>
                (async () => {
                    try {
                        const response = await fetch('${imgUrl}');
                        if (!response.ok) throw new Error("Fetch failed");
                        const blob = await response.blob();
                        const blobUrl = URL.createObjectURL(blob);
                        
                        document.getElementById('loadingUI').style.display = 'none';
                        const isPdf = ${isPdf};

                        if (isPdf) {
                            const frame = document.createElement('iframe');
                            frame.src = blobUrl;
                            frame.style.display = 'none';
                            document.body.appendChild(frame);
                            frame.onload = () => {
                                setTimeout(() => { 
                                    try {
                                        frame.contentWindow.focus();
                                        frame.contentWindow.print();
                                    } catch (e) {
                                        window.print();
                                    }
                                }, 1000);
                            };
                        } else {
                            const img = document.createElement('img');
                            img.src = blobUrl;
                            img.style.display = 'block';
                            img.style.objectFit = 'contain';
                            img.style.padding = '20px';
                            document.body.appendChild(img);
                            setTimeout(() => { window.print(); }, 800);
                        }
                    } catch (err) {
                        console.error("Auto-print failed, falling back to direct:", err);
                        location.href = '${imgUrl}';
                    }
                })();
                </script>
            </body>
            </html>
        `);
        printWindow.document.close();
    };

    const handleDownload = async () => {
        if (!downloadUrl) return;
        setLoading(true);

        try {
            const response = await fetch(downloadUrl);
            if (!response.ok) throw new Error("Download failed");
            
            const blob = await response.blob();
            const blobUrl = window.URL.createObjectURL(blob);
            
            const a = document.createElement('a');
            a.href = blobUrl;
            // The image fallback (licenses/<plate>.png) must not be saved as .pdf.
            a.download = `${plateId}.${downloadUrl.includes('.png') ? 'png' : 'pdf'}`;
            document.body.appendChild(a);
            a.click();
            
            window.URL.revokeObjectURL(blobUrl);
            document.body.removeChild(a);
        } catch (err) {
            window.open(downloadUrl, '_blank');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog
            open
            onClose={onClose}
            size="xl"
            title="License Viewer"
            description={<span className="font-mono">{plateId}</span>}
            bodyClassName="flex flex-col bg-gray-50 h-[70vh]"
            footer={(
                <>
                    <Button onClick={onClose}>Cancel</Button>
                    {downloadUrl && (
                        <Button
                            leadingIcon={<Icon glyph={ICONS.download} size="sm" />}
                            onClick={handleDownload}
                        >
                            Download
                        </Button>
                    )}
                    {downloadUrl && (
                        <Button
                            variant="primary"
                            leadingIcon={<Icon glyph={ICONS.print} size="sm" />}
                            onClick={() => printImage(downloadUrl)}
                        >
                            Direct Print
                        </Button>
                    )}
                </>
            )}
        >
            {loading ? (
                <div className="flex-1 flex items-center justify-center">
                    <LoadingSpinner message="Processing file…" />
                </div>
            ) : downloadUrl ? (
                <div className="flex-1 min-h-0 bg-white border border-gray-200 rounded-lg overflow-hidden">
                    {downloadUrl.includes('.pdf') ? (
                        <iframe
                            src={`${downloadUrl}#toolbar=0`}
                            className="w-full h-full border-0"
                            title="License PDF"
                        />
                    ) : (
                        <div className="w-full h-full flex items-center justify-center p-4">
                            <img
                                src={downloadUrl}
                                className="max-w-full max-h-full object-contain"
                                alt={`License for ${plateId}`}
                            />
                        </div>
                    )}
                </div>
            ) : (
                <div className="flex-1 flex items-center justify-center bg-white border border-gray-200 rounded-lg">
                    <EmptyState
                        icon={ICONS.alert}
                        title="License missing"
                        description={error || "We couldn't find the license for this vehicle in Storage."}
                    />
                </div>
            )}
        </Dialog>
    );
};

export default LicenseModal;
